import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import RestaurantIcon from '@mui/icons-material/Restaurant';

import { getSearchHistory, restoreSearch, deleteSearch } from '../services/searchHistoryApi';
import { usePlanner } from '../contexts/PlannerContext';
import { ShareTripButton } from '../components/common/ShareTripButton';
import { createLogger } from '../utils/logger';
import { surfaceBackgroundSx } from '../theme/backgrounds';
import { blockPadding, sectionContainerSx, sectionStackGap } from '../theme/standards';

interface SearchHistoryItem {
  search_id: string;
  search_type: string;
  created_at: string;
  result_summary: {
    destination_display: string;
    date_range?: string;
    total_days: number;
    total_activities: number;
    total_restaurants: number;
    activity_types?: string[];
    budget?: string;
  };
}

export const SearchHistoryPage: React.FC = () => {
  const { isSignedIn } = useAuth();
  const [searches, setSearches] = useState<SearchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { hydrateFromTravelData } = usePlanner();
  const theme = useTheme();

  useEffect(() => {
    // Clear data when user signs out
    if (!isSignedIn) {
      setSearches([]);
      setLoading(false);
      setError(null);
      return;
    }

    // Load data when signed in
    loadHistory();
  }, [isSignedIn]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSearchHistory(50, 0);
      setSearches(data.searches || []);
    } catch (err: any) {
      log.error('Failed to load history', err);
      setError(err.message || 'Failed to load search history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (searchId: string) => {
    try {
      setRestoringId(searchId);
      setError(null);
      const result = await restoreSearch(searchId);

      // Hydrate the planner context with the restored state
      if (result.travel_state) {
        const restoredSearchId = result.search_history?.search_id ?? searchId;
        const isMultiCity = result.travel_state?.plan_mode === 'multi_city' ||
                            (result.travel_state?.cities?.length ?? 0) > 1 ||
                            Boolean(result.multi_city_plan) ||
                            Boolean(result.country_destination_info || result.travel_state?.country_destination_info);

        // 🔍 [RESTORE] Debug: Log what we received from restoreSearch
        console.log('📦 [RESTORE] Received restoreSearch result (SearchHistoryPage):', {
          searchId,
          isMultiCity,
          hasTopLevelCountryInfo: !!result.country_destination_info,
          hasTopLevelDestinationInfo: !!result.destination_info,
          hasTravelStateCountryInfo: !!result.travel_state.country_destination_info,
          hasTravelStateDestinationInfo: !!result.travel_state.destination_info,
          planMode: result.travel_state?.plan_mode,
          citiesCount: result.travel_state?.cities?.length || 0,
        });

        // Prefer country info for multi-city; check both top-level and nested in travel_state
        // Check top-level first, then fall back to travel_state.country_destination_info
        const countryInfo = isMultiCity
          ? (result.country_destination_info || result.travel_state.country_destination_info || null)
          : null;
        const cityInfo = !isMultiCity
          ? (result.destination_info || result.travel_state.destination_info || null)
          : (countryInfo ? null : (result.destination_info || result.travel_state.destination_info || null));

        console.log('✅ [RESTORE] Extracted destination info (SearchHistoryPage):', {
          countryInfo: countryInfo ? { name: countryInfo.name, hasHistory: !!countryInfo.history } : null,
          cityInfo: cityInfo ? { name: cityInfo.name } : null,
          isMultiCity,
        });

        hydrateFromTravelData(result.travel_state, {
          merge: false,
          searchId: restoredSearchId,
          destinationInfo: cityInfo,
          countryDestinationInfo: countryInfo,
        });

        // Navigate to the planner results page
        const destination = result.travel_state.destination;
        const startDate = result.travel_state.start_date;
        const endDate = result.travel_state.end_date;

        if (destination && startDate && endDate) {
          navigate(`/trip/${restoredSearchId}`);
        } else {
          navigate('/');
        }
      }
    } catch (err: any) {
      log.error('Failed to restore search', err);
      setError(err.message || 'Failed to restore trip');
    } finally {
      setRestoringId(null);
    }
  };

  const handleDeleteClick = (searchId: string) => {
    setSelectedSearchId(searchId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSearchId) return;

    try {
      setDeletingId(selectedSearchId);
      setError(null);
      await deleteSearch(selectedSearchId);
      setDeleteDialogOpen(false);
      setSelectedSearchId(null);
      // Reload the list
      await loadHistory();
    } catch (err: any) {
      log.error('Failed to delete search', err);
      setError(err.message || 'Failed to delete trip');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedSearchId(null);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Loading your trips...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        minHeight: 'calc(100vh - 64px)',
        ...surfaceBackgroundSx(theme),
      })}
    >
      <Box component="section" sx={sectionContainerSx}>
        <Stack spacing={sectionStackGap}>
          {/* Header */}
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              My Trips
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Browse and restore your saved travel plans
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {!loading && searches.length === 0 && (
            <Card
              elevation={0}
              sx={{
                ...blockPadding,
                textAlign: 'center',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <LocalActivityIcon
                sx={{
                  fontSize: 64,
                  color: alpha(theme.palette.primary.main, 0.3),
                  mb: 2,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                No saved trips yet
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Create your first trip to see it here
              </Typography>
              <Button variant="contained" color="primary" onClick={() => navigate('/')}>
                Start Planning
              </Button>
            </Card>
          )}

          {/* Search History Grid */}
          {searches.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: sectionStackGap,
              }}
            >
              {searches.map((search) => (
                <Card
                  key={search.search_id}
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        transform: 'translateY(-4px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 2 }}>
                      {/* Destination */}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <PlaceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {search.result_summary.destination_display}
                        </Typography>
                      </Stack>

                      {/* Date Range */}
                      {search.result_summary.date_range && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {search.result_summary.date_range}
                          </Typography>
                        </Stack>
                      )}

                      {/* Stats */}
                      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <LocalActivityIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {search.result_summary.total_activities} activities
                          </Typography>
                        </Stack>
                        {search.result_summary.total_restaurants > 0 && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <RestaurantIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {search.result_summary.total_restaurants}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      {/* Budget Chip */}
                      {search.result_summary.budget && (
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={search.result_summary.budget}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}
                          />
                        </Box>
                      )}

                      {/* Created Date */}
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Created {formatDate(search.created_at)}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        startIcon={restoringId === search.search_id ? <CircularProgress size={16} /> : <RestoreIcon />}
                        onClick={() => handleRestore(search.search_id)}
                        disabled={restoringId === search.search_id || deletingId === search.search_id}
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          py: 1,
                          flex: '1 1 100%',
                        }}
                      >
                        {restoringId === search.search_id ? 'Restoring...' : 'View Trip'}
                      </Button>
                      <Box sx={{ flex: '1 1 100%' }}>
                        <ShareTripButton
                          searchId={search.search_id}
                          label="Share"
                          size="small"
                          fullWidth
                          disabledLabel="Link unavailable"
                        />
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(search.search_id)}
                        disabled={restoringId === search.search_id || deletingId === search.search_id}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          borderRadius: 2,
                          flex: '1 1 auto',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                          },
                        }}
                        title="Delete trip"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              px: 1,
              py: 1,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Trip?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this trip? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId !== null}
            startIcon={deletingId ? <CircularProgress size={16} /> : null}
            sx={{ fontWeight: 600 }}
          >
            {deletingId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
const log = createLogger('SearchHistoryPage');
